// The phase timeline's layout and the labels the strategy screens share
// (spec §13.3, §13.15): pure, so they are tested without a browser.
// Dates are ISO days (yyyy-mm-dd) and compared as such; nothing here reads
// the clock, the caller passes `today`.

import { draftTimeline } from './strategy';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 86400000;
const day = (iso) => String(iso).slice(0, 10);
const ms = (iso) => Date.parse(`${day(iso)}T00:00:00Z`);
const parts = (iso) => { const d = new Date(ms(iso)); return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() }; };

export const addDays = (iso, n) => new Date(ms(iso) + n * DAY_MS).toISOString().slice(0, 10);
/** The Monday of `iso`'s week. */
export const weekStartOf = (iso) => addDays(iso, -((new Date(ms(iso)).getUTCDay() + 6) % 7));

/** "12–25 Nov", "28 Oct – 3 Nov", "28 Dec 2026 – 3 Jan 2027"; one day is "12 Nov". */
export function windowLabel(start, end) {
  if (!start) return '';
  const a = parts(start);
  if (!end || day(end) === day(start)) return `${a.d} ${MONTHS[a.m]}`;
  const b = parts(end);
  if (a.y !== b.y) return `${a.d} ${MONTHS[a.m]} ${a.y} – ${b.d} ${MONTHS[b.m]} ${b.y}`;
  if (a.m !== b.m) return `${a.d} ${MONTHS[a.m]} – ${b.d} ${MONTHS[b.m]}`;
  return `${a.d}–${b.d} ${MONTHS[b.m]}`;
}

/** "12 Nov" */
export const dayLabel = (iso) => windowLabel(iso, iso);

/**
 * What a strategy proposal changes, said plainly when it is one block or
 * week only (D58); null when Accept replaces the whole plan.
 */
export function scopeNote(body) {
  const scope = body?.scope;
  if (scope !== 'block' && scope !== 'week') return null;
  const what = body.window_start ? windowLabel(body.window_start, body.window_end) : scope === 'week' ? 'one week' : 'one block';
  return `Changes ${what} only; the rest of your plan stays as it is`;
}

const h = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, ''));

/** {min, max, first_week} → "6–9 h a week · 7 h in the first week". */
export function hoursLabel(hours) {
  if (!hours) return null;
  const { min, max, first_week: first } = hours;
  const range = min != null && max != null && min !== max ? `${h(min)}–${h(max)} h a week`
    : max != null ? `Up to ${h(max)} h a week` : min != null ? `${h(min)} h a week` : null;
  const firstText = first != null ? `${h(first)} h in the first week` : null;
  return [range, firstText].filter(Boolean).join(' · ') || null;
}

/**
 * The phases to show: the draft's while one is being written or proposed
 * (marked `draft`, provisional ones flagged), else the committed strategy's.
 */
export function shownPhases(state) {
  const fromDraft = draftTimeline(state?.draft);
  if (fromDraft.length) return { phases: fromDraft, draft: true, stage: state.draft.stage };
  return { phases: (state?.strategy?.timeline || []).map((p) => ({ ...p, provisional: false })), draft: false, stage: null };
}

/** The phase `today` falls in, or null. */
export const phaseOn = (phases, iso) => phases.find((p) => day(p.start_date) <= day(iso) && day(iso) <= day(p.end_date)) || null;

/**
 * The strip: each phase's share of the span from the first phase's start
 * to the later of the last phase's end and the last race in it, in
 * percent; each race in that span placed on it; today's place.
 */
export function layoutTimeline(phases, races = [], today = null) {
  if (!phases.length) return { segments: [], races: [], today: null, start: null, end: null };
  const sorted = [...phases].sort((a, b) => ms(a.start_date) - ms(b.start_date));
  const start = day(sorted[0].start_date);
  const lastEnd = sorted.reduce((m, p) => (ms(p.end_date) > ms(m) ? day(p.end_date) : m), day(sorted[0].end_date));
  const inSpan = races.filter((r) => r?.date && ms(r.date) >= ms(start) && ms(r.date) <= ms(lastEnd) + 7 * DAY_MS);
  const end = inSpan.reduce((m, r) => (ms(r.date) > ms(m) ? day(r.date) : m), lastEnd);
  const span = ms(end) - ms(start) + DAY_MS;
  const at = (iso) => ((ms(iso) - ms(start)) / span) * 100;
  const round = (n) => Math.round(n * 100) / 100;
  return {
    start, end,
    segments: sorted.map((p) => ({
      ...p,
      left: round(at(p.start_date)),
      width: round(((ms(p.end_date) - ms(p.start_date) + DAY_MS) / span) * 100),
      current: Boolean(today) && ms(p.start_date) <= ms(today) && ms(today) <= ms(p.end_date),
    })),
    races: inSpan.map((r) => ({ ...r, left: round(at(r.date) + (DAY_MS / span) * 50) }))
      .sort((a, b) => ms(a.date) - ms(b.date)),
    today: today && ms(today) >= ms(start) && ms(today) <= ms(end) ? round(at(today)) : null,
  };
}

/** The outline week starting `weekStart`: the draft's while one is open, else the committed strategy's. */
export function outlineWeek(state, weekStart) {
  const ws = day(weekStart);
  const find = (weeks) => Object.values(weeks || {}).find((w) => day(w.week_start) === ws) || null;
  return (state?.draft && find(state.draft.weeks)) || find(state?.strategy?.weeks);
}

/**
 * What the calendar says about the week starting `weekStart`: the week's
 * objective (its outline's, else its phase's), and whether it is planned
 * only — a week ahead in a phase with nothing written yet, which the coach
 * fills two weeks ahead.
 */
export function weekNote(state, weekStart, written, today) {
  const { phases } = shownPhases(state);
  const outline = outlineWeek(state, weekStart);
  const phase = phaseOn(phases, weekStart) || phaseOn(phases, addDays(weekStart, 6));
  if (!outline && !phase) return null;
  // A week the strategy has written (materialized) isn't planned-only even before the calendar re-reads it.
  const plannedOnly = written === 0 && ms(weekStart) >= ms(weekStartOf(today)) && !outline?.materialized;
  return {
    objective: outline?.objective || phase?.objective || null,
    phase: phase?.name || null,
    hours: outline?.planned_hours ?? null,
    plannedOnly,
    planned: plannedOnly ? (outline?.sessions || []).length : 0,
  };
}

/** Days until each race from `today` ("in 3 weeks", "in 5 days", "today"). */
export function untilLabel(today, iso) {
  const days = Math.round((ms(iso) - ms(today)) / DAY_MS);
  if (days < 0) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 14) return `in ${days} days`;
  return `in ${Math.floor(days / 7)} weeks`;
}
